import { useMemo, useState, useCallback } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";

const PERSON_KIND = "person";
const SEGMENT_KIND = "people-segment";

const BRAND_ORDER = [
  "TrustOffice", "AeriusView", "StenoDesk", "True Joy Birthing",
  "WingPoint", "TrustMinutes", "SocializeVideo", "Unsorted",
];

const BRAND_TITLE = {
  "TrustOffice": "TrustOffice", "AeriusView": "AeriusView",
  "StenoDesk": "StenoDesk", "True Joy Birthing": "True Joy Birthing",
  "WingPoint": "WingPoint", "TrustMinutes": "TrustMinutes",
  "SocializeVideo": "SocializeVideo", "Unsorted": "Not sorted yet",
};

const TONE_DOT = {
  red: "bg-os-red", amber: "bg-os-amber", gold: "bg-os-gold",
  green: "bg-os-green", muted: "bg-os-ink-4", neutral: "bg-os-ink-4",
};

function fmtStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    weekday: "short", month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const ageHrs = (now - d) / 36e5;
  if (ageHrs < 1) return "just now";
  if (ageHrs < 24) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  if (ageHrs < 48) return "Yesterday " + d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function channelGlyph(channel) {
  if (channel === "phone") return "📞";
  if (channel === "sms") return "💬";
  if (channel === "tidycal") return "📅";
  if (channel === "web_form") return "📝";
  return "✉️";
}

function directionArrow(direction) {
  if (direction === "outbound") return "→";
  if (direction === "inbound") return "←";
  return "↔";
}

function statusWord(status) {
  const map = {
    received: "received", sent: "sent", blocked: "blocked",
    replied: "replied", missed: "missed", failed: "failed",
    confirmed: "confirmed", cancelled: "cancelled",
    completed: "completed", resolved: "resolved",
    waiting: "waiting", handled_verified: "verified",
    handled_answered: "answered", handled_closed: "closed",
    handled_removed: "removed", logged: "logged",
  };
  return map[status] || status;
}

// ── rail button ─────────────────────────────────────────────
function RailBtn({ slug, title, count, tone, active, dashed, onClick, children }) {
  return (
    <button
      type="button"
      data-testid={`people-rail-${slug}`}
      onClick={onClick}
      className={cn(
        "flex h-8 w-full items-center gap-2 rounded-control px-2 text-left text-[13px]",
        dashed && "border border-dashed border-os-border-strong text-os-ink-4",
        active === slug ? "border border-os-border bg-white font-semibold text-os-ink shadow-[0_1px_3px_rgba(0,0,0,0.04)]" : "text-os-ink-2 hover:bg-black/[0.035]",
      )}
    >
      {tone && <span className={cn("h-[7px] w-[7px] shrink-0 rounded-full", TONE_DOT[tone] || "bg-os-ink-4")} />}
      <span className="truncate">{title}</span>
      {count != null && (
        <span className={cn("ml-auto font-mono text-[11px]", active === slug ? "text-os-ink" : "text-os-ink-3")}>
          {count.toLocaleString("en-US")}
        </span>
      )}
    </button>
  );
}

// ── contact row ──────────────────────────────────────────────
function ContactRow({ contact, selected, onSelect }) {
  const p = contact.payload || {};
  const tone = contact.state === "warm" ? "amber" : contact.state === "nothing" ? "red" : "muted";
  return (
    <button
      type="button"
      data-testid="people-contact-row"
      data-state={contact.state}
      onClick={() => onSelect(contact)}
      aria-current={selected ? "true" : undefined}
      className={cn(
        "w-full border-t border-os-border px-3 py-2.5 text-left first:border-t-0",
        "grid grid-cols-[26px_1fr_auto] items-start gap-x-2",
        selected ? "bg-os-wash shadow-[inset_3px_0_0_0_#FFC838]" : "bg-white hover:bg-os-wash/60",
      )}
    >
      <span className={cn("mt-1.5 h-[7px] w-[7px] shrink-0 rounded-full", TONE_DOT[tone] || "bg-os-ink-4")} />
      <span className="min-w-0">
        <span className="flex items-baseline gap-2">
          <span className="truncate text-[13px] font-semibold text-os-ink">{p.name}</span>
          {p.brands && p.brands.length > 0 && (
            <span className="shrink-0 rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
              {p.brands[0]}
            </span>
          )}
          {contact.state === "warm" && (
            <span className="shrink-0 rounded border border-os-amber bg-os-gold-tint px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-amber">
              warm
            </span>
          )}
        </span>
        <span className="mt-0.5 block truncate text-[12px] leading-snug text-os-ink-3">
          {p.lastContactWords ? `Last contact ${p.lastContactWords}` : "Never contacted"}
          {p.openItems > 0 ? ` · ${p.openItems} open item${p.openItems === 1 ? "" : "s"}` : ""}
        </span>
        <span className="mt-0.5 block truncate text-[12px] leading-snug text-os-ink-3">
          {p.nextAction}
        </span>
      </span>
      <span className="whitespace-nowrap text-right font-mono text-[10.5px] leading-relaxed text-os-ink-3">
        {p.lastContactDate || ""}
      </span>
    </button>
  );
}

// ── reader pane ───────────────────────────────────────────────
function Reader({ contact, board, onNotice }) {
  const [showNote, setShowNote] = useState(false);
  const [noteVal, setNoteVal] = useState("");

  if (!contact) {
    return (
      <div className="flex flex-1 items-center justify-center border-os-border bg-white p-10 text-center" data-testid="people-reader-empty">
        <p className="max-w-[32ch] text-[0.875rem] leading-relaxed text-os-ink-3">
          Pick a contact to see their record here. Kit keeps touches in the timeline — the full archive stays in the engine.
        </p>
      </div>
    );
  }

  const p = contact.payload || {};
  const tone = contact.state === "warm" ? "amber" : contact.state === "nothing" ? "red" : "muted";

  const act = async (verb, extra = {}) => {
    const res = await board.act(verb, contact.slug, extra.note);
    if (res) onNotice(null);
    else onNotice("That action didn't go through — try again.");
    return res;
  };

  const timeline = p.timeline || [];

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white" data-testid="people-reader">
      {/* actions */}
      <div className="flex items-center gap-2 border-b border-os-border px-4 py-2.5">
        <span className={cn(
          "hidden rounded-control border border-os-border-strong bg-white px-3 py-1.5 text-[12px] font-semibold xl:inline-block",
          tone === "amber" ? "text-os-amber" : tone === "red" ? "text-os-red" : "text-os-ink-2",
        )}>
          {contact.state === "warm" ? "Warm" : contact.state === "nothing" ? "Nothing queued" : "In touch"}
        </span>
        <button
          type="button"
          data-testid="people-set-aside"
          onClick={() => act("archiveAny")}
          className="rounded-control border border-os-border-strong bg-white px-3 py-1.5 text-[12px] font-semibold text-os-ink shadow-[inset_0_1px_0_rgba(255,255,255,0.95)] hover:bg-os-wash"
        >
          Set aside
        </button>
        <button
          type="button"
          data-testid="people-note-toggle"
          onClick={() => setShowNote((v) => !v)}
          className="rounded-control border border-os-border bg-white px-3 py-1.5 text-[12px] font-medium text-os-ink-2 hover:bg-os-wash"
        >
          {showNote ? "Close note" : "Note"}
        </button>
        <MonoMeta as="span" className="ml-auto">{p.lastContactWords ? `last contact ${p.lastContactWords}` : "never contacted"}</MonoMeta>
      </div>

      {/* note bar */}
      {showNote && (
        <div className="border-b border-os-border bg-os-wash px-4 py-2.5" data-testid="people-note-bar">
          <div className="flex items-center gap-2">
            <input
              value={noteVal}
              onChange={(e) => setNoteVal(e.target.value)}
              placeholder="A note for the record — what this contact needs, what happens next"
              className="h-8 flex-1 rounded-control border border-os-border bg-white px-3 text-[12.5px] text-os-ink placeholder:text-os-ink-4"
            />
            <button
              type="button"
              onClick={async () => {
                if (!noteVal.trim()) return;
                const res = await act("note", { note: noteVal.trim() });
                if (res?.ok) { setNoteVal(""); setShowNote(false); }
              }}
              className="rounded-control bg-os-ink px-3 py-1.5 text-[12px] font-semibold text-white"
            >
              Save note
            </button>
          </div>
        </div>
      )}

      {/* head */}
      <div className="border-b border-os-border px-5 pb-3.5 pt-4">
        <div className="flex items-baseline gap-2">
          <h2 className="text-[19px] font-bold leading-snug tracking-[-0.015em] text-os-ink">{p.name}</h2>
          <span className={cn("rounded px-2 py-0.5 text-[11px] font-semibold",
            tone === "amber" && "bg-[rgba(196,148,28,0.08)] text-os-amber",
            tone === "red" && "bg-[rgba(194,43,16,0.08)] text-os-red",
            tone === "muted" && "bg-os-wash text-os-ink-3",
          )}>
            {contact.state === "warm" ? "Warm" : contact.state === "nothing" ? "Nothing queued" : "In touch"}
          </span>
        </div>
        <div className="mt-2 flex items-center gap-2.5">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-bronze bg-gold-tint text-[11px] font-bold text-[#8A6A14]">
            {(p.name || "?").slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-os-ink">
              {p.email || "No email on file"} <span className="font-normal text-os-ink-3">· {p.brands?.[0] || "Not sorted yet"}</span>
            </div>
            <div className="font-mono text-[10.5px] text-os-ink-3">{p.phone || ""} {p.company ? `· ${p.company}` : ""}</div>
          </div>
        </div>
        {(p.aliases && p.aliases.length > 0) && (
          <p className="mt-2 text-[12px] text-os-ink-3">
            Also reaches: {p.aliases.join(", ")}
          </p>
        )}
      </div>

      {/* body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* field grid */}
        <div className="mb-5 grid grid-cols-2 gap-3">
          {[
            { label: "Status", value: p.status || "—" },
            { label: "Title", value: p.title || "—" },
            { label: "Company", value: p.company || "—" },
            { label: "Phone", value: p.phone || "—" },
          ].map((s) => (
            <div key={s.label} className="rounded-control border border-os-border bg-os-wash px-3 py-2">
              <p className="text-[0.9375rem] font-semibold text-os-ink">{s.value}</p>
              <p className="font-mono text-[0.625rem] uppercase tracking-[0.08em] text-os-ink-4">{s.label}</p>
            </div>
          ))}
        </div>

        {/* timeline */}
        <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Timeline</div>
        {timeline.length === 0 && (
          <p className="text-[13px] text-os-ink-3">No interactions in this window — the full archive stays in the engine.</p>
        )}
        <div className="flex flex-col gap-3">
          {timeline.map((t) => (
            <div key={t.id} className="flex gap-3 border-b border-os-border pb-3 last:border-b-0">
              <span className="mt-0.5 shrink-0 text-[13px]">{channelGlyph(t.channel)}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="text-[13px] font-semibold text-os-ink">{directionArrow(t.direction)} {t.subject}</span>
                </div>
                <p className="mt-0.5 text-[12px] leading-snug text-os-ink-3">{t.summary}</p>
                <div className="mt-1 flex items-center gap-2">
                  <span className="rounded border border-os-border px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-ink-4">
                    {statusWord(t.status)}
                  </span>
                  {t.nextAction && t.nextAction !== "closed" && (
                    <span className="rounded border border-os-amber bg-os-gold-tint px-1 py-px font-mono text-[9px] uppercase tracking-[0.06em] text-os-amber">
                      {t.nextAction}
                    </span>
                  )}
                  <span className="font-mono text-[10px] text-os-ink-3">{fmtWhen(t.sentAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {p.tags && p.tags.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-4">Tags</div>
            <div className="flex flex-wrap gap-2">
              {p.tags.map((t) => (
                <span key={t} className="rounded-pill bg-os-wash border border-os-border px-2 py-1 text-[0.8125rem] text-os-ink-2">{t}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── the client ───────────────────────────────────────────────
export function PeopleClient({ items, meta, board, variant = "desktop", onNotice, brandScope }) {
  const desktop = useMediaQuery("(min-width: 1024px)") && variant === "desktop";
  const isScoped = !!brandScope;
  const [search, setSearch] = useState("");
  const segments = useMemo(() => items.filter((i) => i.kind === SEGMENT_KIND), [items]);
  const contacts = useMemo(() => items.filter((i) => i.kind === PERSON_KIND), [items]);

  // Scoped default: show the brand segment
  const [active, setActive] = useState(isScoped ? `brand:${brandScope.name}` : "segment:warm");
  const [selectedSlug, setSelectedSlug] = useState(null);

  const activeBrand = active?.startsWith("brand:") ? active.slice(6) : null;

  const visibleContacts = useMemo(() => {
    let pool = contacts;
    if (active === "segment:warm") {
      pool = pool.filter((c) => c.state === "warm");
    } else if (active === "segment:needs-reply") {
      pool = pool.filter((c) => {
        if ((c.payload?.openItems || 0) > 0 && c.state === "warm") return true;
        return (c.payload?.timeline || []).some((t) => t.nextAction === "awaiting_reply");
      });
    } else if (active === "segment:call-backs") {
      pool = pool.filter((c) => {
        const tl = c.payload?.timeline || [];
        return tl.some((t) => ["phone", "sms", "call"].includes(t.channel) && t.direction === "inbound" && ["received", "missed", "blocked"].includes(t.status));
      });
    } else if (active === "segment:waiting-30d") {
      pool = pool.filter((c) => {
        const tl = c.payload?.timeline || [];
        return tl.some((t) => t.ageDays != null && t.ageDays > 30 && t.nextAction && t.nextAction !== "closed");
      });
    } else if (active === "segment:never-contacted") {
      pool = pool.filter((c) => !c.payload?.lastContactDate);
    } else if (active === "segment:opted-out") {
      pool = pool.filter((c) => c.payload?.status === "opted_out");
    } else if (active === "segment:system-senders") {
      pool = pool.filter((c) => {
        const e = (c.payload?.email || "").toLowerCase();
        const n = (c.payload?.name || "").toLowerCase();
        if (!e && !n) return false;
        if (!n || !n.trim()) return true;
        const machineDomains = {"google.com":1, "yahoo.com":1, "fastmaildmarc.com":1, "grammarly.com":1, "skool.com":1, "instagram.com":1, "facebook.com":1, "meta.com":1};
        const domain = e.includes("@") ? e.split("@").pop() : "";
        if (machineDomains[domain]) return true;
        if (e.includes("noreply") || e.includes("no-reply") || e.includes("dmarc") || e.includes("reports@")) return true;
        const machineNames = {"google":1, "grammarly":1, "instagram":1, "facebook":1, "meta":1, "dmarc aggregate report":1};
        if (machineNames[n]) return true;
        return false;
      });
    } else if (activeBrand) {
      pool = pool.filter((c) => (c.payload?.brands || []).includes(activeBrand));
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      pool = pool.filter((c) => {
        const p = c.payload || {};
        const terms = filterNone([p.name, p.email, p.company, p.title, ...(p.aliases || []), ...(p.tags || [])]).join(" ").toLowerCase();
        return terms.includes(q);
      });
    }
    return pool;
  }, [contacts, active, activeBrand, search]);

  const selected = useMemo(
    () => visibleContacts.find((c) => c.slug === selectedSlug) || visibleContacts[0] || null,
    [visibleContacts, selectedSlug],
  );

  function filterNone(arr) {
    return arr.filter((x) => x != null && x !== "");
  }

  // Context header text
  const contextHeader = useMemo(() => {
    if (isScoped && activeBrand) {
      const brandTotal = contacts.filter((c) => (c.payload?.brands || []).includes(activeBrand)).length;
      return `${activeBrand} — ${brandTotal} contacts`;
    }
    if (active === "segment:warm") {
      return `${meta?.warm_count || 0} warm right now`;
    }
    if (active === "segment:needs-reply") {
      return `${meta?.needs_reply_count || 0} need a reply`;
    }
    if (active === "segment:call-backs") {
      return `${meta?.call_backs_count || 0} call-backs owed`;
    }
    if (active === "segment:waiting-30d") {
      return `${meta?.waiting_30d_count || 0} waiting 30 days +`;
    }
    if (active === "segment:never-contacted") {
      return `${meta?.never_contacted_count || 0} never contacted`;
    }
    if (active === "segment:opted-out") {
      return `${meta?.opted_out_count || 0} opted out`;
    }
    if (active === "segment:system-senders") {
      return `${meta?.system_senders_count || 0} system senders`;
    }
    if (activeBrand) {
      const brandTotal = contacts.filter((c) => (c.payload?.brands || []).includes(activeBrand)).length;
      return `${activeBrand} — ${brandTotal} contacts`;
    }
    return "";
  }, [active, activeBrand, meta, contacts, isScoped]);

  // Rail grouping
  const needsYou = segments.filter((s) => ["segment:warm", "segment:needs-reply", "segment:call-backs", "segment:waiting-30d"].includes(s.slug));
  const brandSegs = BRAND_ORDER.map((b) => segments.find((s) => s.slug === `brand:${b}`)).filter(Boolean);
  const quietSegs = segments.filter((s) => ["segment:never-contacted", "segment:opted-out", "segment:system-senders"].includes(s.slug));

  // Scoped rail: only show the current brand segment, no other brands
  const visibleBrandSegs = isScoped
    ? brandSegs.filter((s) => s.slug === `brand:${brandScope.name}`)
    : brandSegs;

  const headerTitle = isScoped ? `${brandScope.name} — People` : "People";

  return (
    <div data-testid="people-client" className="w-full text-os-ink">
      {/* top strip */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <h1 className="text-[15px] font-bold tracking-[-0.01em] text-os-ink">{headerTitle}</h1>
        <MonoMeta data-testid="people-as-of" as="p" className="whitespace-nowrap">
          counts from {fmtStamp(meta?.source_stamp || meta?.ingested_at)}
        </MonoMeta>
        <span className="ml-auto rounded border border-os-border bg-os-wash px-2 py-1 font-mono text-[10.5px] text-os-ink-2">
          {(meta?.warm_count ?? 0).toLocaleString("en-US")} warm ·{" "}
          {(meta?.in_touch_count ?? 0).toLocaleString("en-US")} in touch ·{" "}
          {(meta?.nothing_queued_count ?? 0).toLocaleString("en-US")} nothing queued
        </span>
      </div>

      <div
        className={cn(
          "grid min-h-[560px] overflow-hidden rounded-card border border-os-border",
          desktop ? "grid-cols-[230px_430px_1fr]" : "grid-cols-1",
        )}
        style={{ height: desktop ? "min(78vh, 860px)" : undefined }}
      >
        {/* pane 1 — segments */}
        <aside className="hidden min-h-0 flex-col overflow-y-auto border-r border-os-border bg-os-wash p-2.5 md:flex">
          {!isScoped && (
            <>
              <div className="px-1.5 pb-1.5 pt-1 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
                Needs you
              </div>
              {needsYou.map((s) => (
                <RailBtn
                  key={s.slug}
                  slug={s.slug}
                  title={s.payload.title}
                  count={s.payload.count}
                  tone={{ "segment:warm": "amber", "segment:needs-reply": "red", "segment:call-backs": "gold", "segment:waiting-30d": "amber" }[s.slug] || "muted"}
                  active={active}
                  onClick={() => { setActive(s.slug); setSelectedSlug(null); }}
                />
              ))}
              <div className="mx-1.5 my-2.5 h-px bg-os-border" />
            </>
          )}
          <div className="px-1.5 pb-1.5 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
            Brands
          </div>
          {visibleBrandSegs.map((s) => (
            <RailBtn
              key={s.slug}
              slug={s.slug}
              title={BRAND_TITLE[s.payload.title] || s.payload.title}
              count={s.payload.count}
              active={active}
              onClick={() => { setActive(s.slug); setSelectedSlug(null); }}
            />
          ))}
          {!isScoped && (
            <>
              <div className="mx-1.5 my-2.5 h-px bg-os-border" />
              <div className="px-1.5 pb-1.5 font-mono text-[10px] uppercase tracking-[0.09em] text-os-ink-4">
                Quiet
              </div>
              {quietSegs.map((s) => (
                <RailBtn
                  key={s.slug}
                  slug={s.slug}
                  title={s.payload.title}
                  count={s.payload.count}
                  tone="muted"
                  active={active}
                  onClick={() => { setActive(s.slug); setSelectedSlug(null); }}
                />
              ))}
            </>
          )}
          <div className="px-1.5 pb-1 pt-2 text-[10.5px] leading-relaxed text-os-ink-4">
            {isScoped
              ? `Scoped to ${brandScope.name} — other brands hidden.`
              : "One pool across every brand — each contact's real name and history live in the reader pane."}
          </div>
        </aside>

        {/* pane 2 — contact list */}
        <section className={cn("flex min-h-0 flex-col border-os-border bg-white md:border-r", !desktop && "hidden")} data-testid="people-list">
          <div className="flex h-11 items-center gap-2.5 border-b border-os-border px-3.5">
            <span className="text-[14px] font-semibold text-os-ink">{contextHeader}</span>
            <span className="font-mono text-[10px] text-os-ink-3">newest first</span>
            <span className="ml-auto font-mono text-[10px] text-os-ink-4">
              {visibleContacts.length} shown
            </span>
          </div>
          {/* search */}
          <div className="border-b border-os-border bg-os-wash px-3 py-2">
            <input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setSelectedSlug(null); }}
              placeholder="Search name, email, company…"
              className="h-8 w-full rounded-control border border-os-border bg-white px-3 text-[12.5px] text-os-ink placeholder:text-os-ink-4"
              data-testid="people-search"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {visibleContacts.length === 0 && (
              <div className="p-6 text-center text-[13px] leading-relaxed text-os-ink-3" data-testid="people-list-empty">
                {isScoped
                  ? `No people filed for ${brandScope.name} yet.`
                  : "Nothing in this view right now — the list fills on Kit's next sync."}
              </div>
            )}
            <div>
              {visibleContacts.map((c) => (
                <ContactRow key={c.slug} contact={c} selected={selected?.slug === c.slug} onSelect={(x) => setSelectedSlug(x.slug)} />
              ))}
            </div>
          </div>
        </section>

        {/* pane 3 — reader */}
        <Reader contact={selected} board={board} onNotice={onNotice} />
      </div>
    </div>
  );
}
